import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, relTime } from '../lib/api'
import { cm, type CmMailbox } from '../lib/cm'

/**
 * Mailboxes on the cPanel host that the panel logs in to.
 *
 * Two ways in: connect a mailbox that already exists (its password is tested once, then
 * stored encrypted), or create a NEW one on cPanel from here. Nothing on this page edits or
 * deletes a mailbox on cPanel — "Disconnect" only removes it from the panel.
 */
export function Mailboxes() {
  const qc = useQueryClient()
  const { data: setup } = useQuery({ queryKey: ['cm', 'setup'], queryFn: cm.setup })
  const { data, isLoading } = useQuery({
    queryKey: ['cm', 'mailboxes'], queryFn: cm.mailboxes, refetchInterval: 15_000,
  })
  const [mode, setMode] = useState<'none' | 'connect' | 'create'>('none')
  const refresh = () => qc.invalidateQueries({ queryKey: ['cm'] })

  const rows = data?.data ?? []

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Mailboxes</h1>
          <p className="lede">
            Mailboxes on your cPanel host. Campaigns and replies are sent through each mailbox's own
            SMTP, so the domain's existing SPF and DKIM sign them. Inbox and Sent are mirrored into{' '}
            <Link to="/mail">Mail</Link> every couple of minutes.
          </p>
        </div>
        <div className="row">
          <button className="secondary" onClick={() => setMode(mode === 'connect' ? 'none' : 'connect')}>Connect existing</button>
          <button onClick={() => setMode(mode === 'create' ? 'none' : 'create')} disabled={!setup?.cpanel_configured}
                  title={setup?.cpanel_configured ? '' : 'cPanel access is not set up on the server'}>
            New mailbox on cPanel
          </button>
        </div>
      </div>

      {setup && setup.usage.length > 0 && (
        <div className="kpis" style={{ marginBottom: 20 }}>
          {setup.usage.map(u => (
            <div className="kpi" key={u.host}>
              <div className="kpi-body">
                <span className="kpi-value">{u.last_hour} / {u.hourly_cap}</span>
                <span className="kpi-label">Sent this hour via {u.host}</span>
                <span className="kpi-sub">{u.last_day} / {u.daily_cap} in the last 24h — shared by every domain on that host</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'connect' && <ConnectForm defaults={setup} onDone={() => { setMode('none'); refresh() }} />}
      {mode === 'create' && setup && <CreateForm setup={setup} onDone={() => { setMode('none'); refresh() }} />}

      {isLoading && <div className="skeleton" style={{ height: 200 }} />}
      {!isLoading && rows.length === 0 && (
        <div className="empty">
          <div className="big">✉</div>
          <p><strong>No mailboxes connected yet</strong></p>
          <p>Connect an existing mailbox such as hello@yourdomain, or create a new one for campaigns, e.g. news@.</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="card flush">
          <table className="data">
            <thead>
              <tr><th>Mailbox</th><th>Status</th><th>Incoming</th><th>Outgoing</th><th>Last sync</th><th /></tr>
            </thead>
            <tbody>
              {rows.map(m => <MailboxRow key={m.id} m={m} onChange={refresh} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function MailboxRow({ m, onChange }: { m: CmMailbox; onChange: () => void }) {
  const [fixing, setFixing] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const update = useMutation({
    mutationFn: (b: Parameters<typeof cm.updateMailbox>[1]) => cm.updateMailbox(m.id, b),
    onSuccess: () => { setFixing(false); setPassword(''); setError(null); onChange() },
    onError: e => setError((e as ApiError).message),
  })
  const sync = useMutation({ mutationFn: () => cm.sync(m.id), onSuccess: onChange })
  const disconnect = useMutation({
    mutationFn: () => cm.disconnect(m.id), onSuccess: onChange,
    onError: e => alert((e as ApiError).message),
  })

  const [cls, text] = m.auth_failed ? ['failed', 'Password rejected']
    : m.last_sync_error ? ['pending', 'Sync problem']
    : !m.sending_enabled ? ['neutral', 'Sending off']
    : m.last_sync_at ? ['ok', 'Connected'] : ['info', 'First sync…']

  return (
    <>
      <tr>
        <td>
          <div className="mono small">{m.email}</div>
          <div className="small muted">
            {m.display_name ?? '—'} · {m.smtp_host}:{m.smtp_port}
            {m.created_on_cpanel && <> · <span className="badge info">created here</span></>}
          </div>
        </td>
        <td>
          <span className={`badge ${cls}`}>{text}</span>
          {m.last_sync_error && <div className="small muted truncate" title={m.last_sync_error}>{m.last_sync_error}</div>}
        </td>
        <td>
          <Link to={`/mail?mailbox=${m.id}&direction=in`}>{m.incoming.toLocaleString()}</Link>
          {m.unread > 0 && <span className="small muted"> · {m.unread} unread</span>}
        </td>
        <td><Link to={`/mail?mailbox=${m.id}&direction=out`}>{m.outgoing.toLocaleString()}</Link></td>
        <td className="small muted">{m.last_sync_at ? relTime(m.last_sync_at) : 'never'}</td>
        <td className="right" style={{ whiteSpace: 'nowrap' }}>
          <button className="ghost" onClick={() => sync.mutate()} disabled={m.auth_failed}>Sync now</button>
          <button className="ghost" onClick={() => update.mutate({ sending_enabled: !m.sending_enabled })}>
            {m.sending_enabled ? 'Pause sending' : 'Allow sending'}
          </button>
          <button className="ghost" onClick={() => setFixing(f => !f)}>Password</button>
          <button className="ghost" onClick={() => {
            if (confirm(`Remove ${m.email} from the panel?\n\nIt is NOT deleted on cPanel — its mail stays there.`)) disconnect.mutate()
          }}>Disconnect</button>
        </td>
      </tr>
      {fixing && (
        <tr>
          <td colSpan={6}>
            <form className="row" onSubmit={e => { e.preventDefault(); if (password) update.mutate({ password }) }}>
              <input type="password" placeholder={`Current password of ${m.email}`} value={password}
                     onChange={e => setPassword(e.target.value)} style={{ flex: '1 1 260px' }} autoComplete="off" />
              <button type="submit" disabled={!password || update.isPending}>{update.isPending ? 'Testing…' : 'Test & save'}</button>
              <span className="small muted">Saves our copy only. The password on cPanel is not changed.</span>
            </form>
            {error && <div className="alert error" style={{ marginTop: 8 }}>{error}</div>}
          </td>
        </tr>
      )}
    </>
  )
}

function ConnectForm({ defaults, onDone }: { defaults?: { default_smtp_host?: string | null; default_smtp_port: number; default_imap_host?: string | null; default_imap_port: number }; onDone: () => void }) {
  const [f, setF] = useState({
    email: '', password: '', display_name: '',
    smtp_host: defaults?.default_smtp_host ?? '', smtp_port: defaults?.default_smtp_port ?? 465,
    imap_host: defaults?.default_imap_host ?? '', imap_port: defaults?.default_imap_port ?? 993,
  })
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => cm.connect({ ...f, display_name: f.display_name || undefined }),
    onSuccess: onDone,
    onError: e => setError((e as ApiError).message),
  })
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF({ ...f, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.value })

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h2 style={{ marginTop: 0 }}>Connect an existing mailbox</h2>
      <p className="small muted">
        The password is tested once against IMAP and SMTP before anything is saved. A wrong password is
        never retried — cPanel locks the whole account after repeated failures.
      </p>
      <form onSubmit={e => { e.preventDefault(); save.mutate() }}>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 240px' }}><label>Email address</label>
            <input type="email" required value={f.email} onChange={set('email')} placeholder="hello@example.com" /></div>
          <div className="field" style={{ flex: '1 1 200px' }}><label>Password</label>
            <input type="password" required value={f.password} onChange={set('password')} autoComplete="off" /></div>
          <div className="field" style={{ flex: '1 1 200px' }}><label>Display name (optional)</label>
            <input value={f.display_name} onChange={set('display_name')} placeholder="Acme Team" /></div>
        </div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '2 1 220px' }}><label>SMTP server</label>
            <input required value={f.smtp_host} onChange={set('smtp_host')} /></div>
          <div className="field" style={{ flex: '0 1 100px' }}><label>Port</label>
            <input type="number" value={f.smtp_port} onChange={set('smtp_port')} /></div>
          <div className="field" style={{ flex: '2 1 220px' }}><label>IMAP server</label>
            <input required value={f.imap_host} onChange={set('imap_host')} /></div>
          <div className="field" style={{ flex: '0 1 100px' }}><label>Port</label>
            <input type="number" value={f.imap_port} onChange={set('imap_port')} /></div>
        </div>
        {error && <div className="alert error">{error}</div>}
        <button type="submit" disabled={save.isPending}>{save.isPending ? 'Testing login…' : 'Test & connect'}</button>
      </form>
    </div>
  )
}

function CreateForm({ setup, onDone }: { setup: { allowed_domains: string[]; default_quota_mb: number }; onDone: () => void }) {
  const [local, setLocal] = useState('news')
  const [domain, setDomain] = useState(setup.allowed_domains[0] ?? '')
  const [name, setName] = useState('')
  const [quota, setQuota] = useState(setup.default_quota_mb)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => cm.create({ local_part: local, domain, display_name: name || undefined, quota_mb: quota }),
    onSuccess: r => {
      setError(null)
      setResult(r.warning ? `${r.email} was created. Note: ${r.warning} It will be retried automatically.` : `${r.email} was created and connected.`)
      setTimeout(onDone, 2500)
    },
    onError: e => setError((e as ApiError).message),
  })

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h2 style={{ marginTop: 0 }}>New mailbox on cPanel</h2>
      <p className="small muted">
        Creates the mailbox on cPanel and connects it here in one step. The password is generated and
        stored encrypted — you never need to type it. Existing mailboxes are never touched.
      </p>
      <form className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}
            onSubmit={e => { e.preventDefault(); create.mutate() }}>
        <div className="field" style={{ flex: '1 1 140px', marginBottom: 0 }}><label>Address</label>
          <input required value={local} onChange={e => setLocal(e.target.value.toLowerCase())} /></div>
        <div className="field" style={{ flex: '1 1 220px', marginBottom: 0 }}><label>Domain</label>
          <select value={domain} onChange={e => setDomain(e.target.value)}>
            {setup.allowed_domains.map(d => <option key={d} value={d}>@{d}</option>)}
          </select></div>
        <div className="field" style={{ flex: '1 1 180px', marginBottom: 0 }}><label>Display name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Acme News" /></div>
        <div className="field" style={{ flex: '0 1 120px', marginBottom: 0 }}><label>Quota (MB)</label>
          <input type="number" min={50} max={2048} value={quota} onChange={e => setQuota(Number(e.target.value))} /></div>
        <button type="submit" disabled={create.isPending || !local || !domain}>
          {create.isPending ? 'Creating…' : `Create ${local || '…'}@${domain}`}
        </button>
      </form>
      {error && <div className="alert error" style={{ marginTop: 12 }}>{error}</div>}
      {result && <div className="alert" style={{ marginTop: 12 }}>{result}</div>}
    </div>
  )
}
